export const DEFAULT_MATTE_COLOR = "#09080f";
export const TRANSPARENT_MATTE_COLOR = "transparent";

export function isTransparentMatte(color) {
  const value = String(color || "")
    .trim()
    .toLowerCase();
  return value === "transparent" || value === "rgba(0, 0, 0, 0)";
}

function parseCssColorRgb(color) {
  const value = String(color || "")
    .trim()
    .toLowerCase();
  if (!value || isTransparentMatte(value) || value === "black" || value === "white") {
    if (value === "black") return [0, 0, 0];
    if (value === "white") return [255, 255, 255];
    return null;
  }
  const hex = value.replace("#", "");
  if (/^[0-9a-f]{3}$/.test(hex)) {
    return [
      parseInt(hex[0] + hex[0], 16),
      parseInt(hex[1] + hex[1], 16),
      parseInt(hex[2] + hex[2], 16),
    ];
  }
  if (/^[0-9a-f]{6}$/.test(hex)) {
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }
  const rgb = value.match(
    /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/,
  );
  if (rgb) {
    return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  }
  return null;
}

function matteLuminance(rgb) {
  const [r, g, b] = rgb;
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

/** True for black and near-black mattes (e.g. #000, #09080f). */
export function isBlackShadeMatte(color) {
  const rgb = parseCssColorRgb(color);
  if (!rgb) return false;
  return matteLuminance(rgb) <= 0.12;
}

/** True for white and near-white mattes (e.g. #fff, #fefefe). */
export function isWhiteShadeMatte(color) {
  const rgb = parseCssColorRgb(color);
  if (!rgb) return false;
  const [r, g, b] = rgb;
  return matteLuminance(rgb) >= 0.9 && Math.max(r, g, b) - Math.min(r, g, b) <= 20;
}

export const FOCUS_BORDER_ON_BLACK = "rgba(255, 255, 255, 0.402)";
export const FOCUS_BORDER_ON_WHITE = "rgba(5, 5, 5, 0.402)";
export const FOCUS_BORDER_ON_COLOR = "rgba(255, 255, 255, 0.802)";

/** Preview-only focus wash: preserve the matte hue while mixing in 10% white. */
export function getFocusOverlayColor(color, lightenAmount = 0.1) {
  const rgb = parseCssColorRgb(color) || parseCssColorRgb(DEFAULT_MATTE_COLOR);
  const amount = Math.max(0, Math.min(1, Number(lightenAmount) || 0));
  const lighter = rgb.map((channel) =>
    Math.round(channel + (255 - channel) * amount),
  );
  return `rgb(${lighter.join(", ")})`;
}

export function getFocusBorderColor(color) {
  if (isWhiteShadeMatte(color)) return FOCUS_BORDER_ON_WHITE;
  return isBlackShadeMatte(color)
    ? FOCUS_BORDER_ON_BLACK
    : FOCUS_BORDER_ON_COLOR;
}

export function resolveMatteColor(settings) {
  const color = settings?.matte_color;
  if (isTransparentMatte(color)) return TRANSPARENT_MATTE_COLOR;
  return color || DEFAULT_MATTE_COLOR;
}

/** Never transparent — used when the render format has no alpha channel. */
export function resolveOpaqueMatteColor(settings) {
  const color = resolveMatteColor(settings);
  if (isTransparentMatte(color)) return DEFAULT_MATTE_COLOR;
  return color;
}

const BACKGROUND_STYLE_KEYS = [
  "backgroundColor",
  "backgroundImage",
  "backgroundSize",
  "backgroundPosition",
  "backgroundRepeat",
];

export function getProjectBackgroundStyle(settings) {
  const safeSettings = settings ?? {};
  const backgroundColor = resolveMatteColor(safeSettings);
  const image = safeSettings.background_image || "";
  return {
    backgroundColor,
    ...(image
      ? {
          backgroundImage: `url(${image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }
      : {}),
  };
}

/** Per-block background for scroll render — one continuous image across all blocks. */
export function getScrollBlockBackgroundStyle(settings, offsetY, videoWidth) {
  const safeSettings = settings ?? {};
  const backgroundColor = resolveMatteColor(safeSettings);
  const image = safeSettings.background_image || "";

  if (!image) {
    return { backgroundColor };
  }

  return {
    backgroundColor,
    backgroundImage: `url(${image})`,
    backgroundSize: `${videoWidth}px auto`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: `center ${-offsetY}px`,
  };
}

export function snapshotBackgroundStyles(element) {
  const snapshot = {};
  for (const key of BACKGROUND_STYLE_KEYS) {
    snapshot[key] = element.style[key] || "";
  }
  return snapshot;
}

export function applyBackgroundStyles(element, backgroundStyle = {}) {
  for (const key of BACKGROUND_STYLE_KEYS) {
    if (backgroundStyle[key]) {
      element.style[key] = backgroundStyle[key];
    } else {
      element.style[key] = "";
    }
  }
}

export function restoreBackgroundStyles(element, snapshot = {}) {
  for (const key of BACKGROUND_STYLE_KEYS) {
    element.style[key] = snapshot[key] || "";
  }
}

export function getHtml2CanvasBackgroundColor(backgroundStyle = {}) {
  if (backgroundStyle.backgroundImage) return null;
  const color = backgroundStyle.backgroundColor;
  if (!color || color === "transparent" || color === "rgba(0, 0, 0, 0)") {
    return null;
  }
  return color;
}
